/**
 * reverseGeocode — Geocodificación inversa vía CartoCiudad
 *
 * Función autocontenida para obtener la dirección más cercana a unas
 * coordenadas dadas. Pensada para integración en el visor de edición:
 * cuando el usuario arrastra un marcador, se llama a esta función
 * para mostrar la dirección correspondiente a la nueva posición.
 *
 * Flujo: EPSG:25830 (x,y) → WGS84 (lon,lat) → CartoCiudad reverse → dirección
 *
 * No depende de src/services/geocoding/ (evita conflictos con M3).
 *
 * @module components/map/editing/reverseGeocode
 * @version 1.0.0
 * @date 2026-02-11
 * @session F8.04 - Geocodificación inversa en modo edición
 */

import proj4 from 'proj4';

// =============================================================================
// DEFINICIONES PROJ4 (autocontenidas para no depender de lib/)
// =============================================================================

const EPSG_25830 = 'EPSG:25830';
const EPSG_4326 = 'EPSG:4326';

// Registrar solo si no están ya definidas
if (!proj4.defs(EPSG_25830)) {
  proj4.defs(EPSG_25830, '+proj=utm +zone=30 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs');
}
if (!proj4.defs(EPSG_4326)) {
  proj4.defs(EPSG_4326, '+proj=longlat +datum=WGS84 +no_defs +type=crs');
}

// =============================================================================
// CONSTANTES
// =============================================================================

const CARTOCIUDAD_REVERSE_URL =
  'https://www.cartociudad.es/geocoder/api/geocoder/reverseGeocode';

/** Timeout por defecto (ms) */
const DEFAULT_TIMEOUT = 5000;

// =============================================================================
// TIPOS
// =============================================================================

/** Coordenadas de entrada en EPSG:25830 */
export interface ReverseGeocodeInput {
  x: number;
  y: number;
}

/** Resultado de geocodificación inversa */
export interface ReverseGeocodeResult {
  /** Dirección completa formateada */
  address: string;
  /** Tipo de vía (calle, avenida, plaza, etc.) */
  tip_via?: string;
  /** Nombre de la vía */
  streetName?: string;
  /** Número de portal */
  portalNumber?: string;
  /** Municipio */
  municipality?: string;
  /** Provincia */
  province?: string;
  /** Código postal */
  postalCode?: string;
  /** Coordenadas WGS84 devueltas por el servicio */
  wgs84: { lon: number; lat: number };
  /** Tipo de localización ('portal', 'tramo', 'municipio', etc.) */
  locationType?: string;
  /** Estado de la respuesta */
  state?: number;
}

/** Error de geocodificación inversa */
export interface ReverseGeocodeError {
  type: 'network' | 'timeout' | 'no-result' | 'invalid-coords' | 'unknown';
  message: string;
}

/** Opciones de la función */
export interface ReverseGeocodeOptions {
  /** Timeout en ms (default: 5000) */
  timeout?: number;
  /** AbortSignal para cancelación */
  signal?: AbortSignal;
}

// =============================================================================
// VALIDACIÓN
// =============================================================================

/** Rangos válidos EPSG:25830 para Andalucía (con margen) */
const ANDALUCIA_X_MIN = 100_000;
const ANDALUCIA_X_MAX = 620_000;
const ANDALUCIA_Y_MIN = 3_980_000;
const ANDALUCIA_Y_MAX = 4_300_000;

function isValidEPSG25830(x: number, y: number): boolean {
  return (
    Number.isFinite(x) &&
    Number.isFinite(y) &&
    x >= ANDALUCIA_X_MIN &&
    x <= ANDALUCIA_X_MAX &&
    y >= ANDALUCIA_Y_MIN &&
    y <= ANDALUCIA_Y_MAX
  );
}

// =============================================================================
// CONVERSIÓN
// =============================================================================

/** Convierte EPSG:25830 → WGS84 (lon, lat) */
export function toWGS84(x: number, y: number): { lon: number; lat: number } {
  const [lon, lat] = proj4(EPSG_25830, EPSG_4326, [x, y]);
  return { lon, lat };
}

// =============================================================================
// FUNCIÓN PRINCIPAL
// =============================================================================

/**
 * Geocodificación inversa: coordenadas EPSG:25830 → dirección
 *
 * @param input Coordenadas X, Y en EPSG:25830
 * @param options Opciones (timeout, signal)
 * @returns Resultado o error discriminado
 */
export async function reverseGeocode(
  input: ReverseGeocodeInput,
  options: ReverseGeocodeOptions = {},
): Promise<{ ok: true; data: ReverseGeocodeResult } | { ok: false; error: ReverseGeocodeError }> {
  const { x, y } = input;
  const timeout = options.timeout ?? DEFAULT_TIMEOUT;

  // --- Validar coordenadas ---
  if (!isValidEPSG25830(x, y)) {
    return {
      ok: false,
      error: {
        type: 'invalid-coords',
        message: `Coordenadas fuera de rango EPSG:25830 Andalucía: X=${x}, Y=${y}`,
      },
    };
  }

  // --- Convertir a WGS84 ---
  const { lon, lat } = toWGS84(x, y);

  // --- Construir URL ---
  const url = `${CARTOCIUDAD_REVERSE_URL}?lon=${lon}&lat=${lat}`;

  // --- Fetch con timeout ---
  const controller = new AbortController();
  const signal = options.signal
    ? combineAbortSignals(options.signal, controller.signal)
    : controller.signal;

  const timer = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, { signal });

    if (!response.ok) {
      return {
        ok: false,
        error: {
          type: 'network',
          message: `CartoCiudad respondió ${response.status}: ${response.statusText}`,
        },
      };
    }

    const data = await response.json();

    // CartoCiudad devuelve state=0 cuando no encuentra nada
    if (!data || data.state === 0 || !data.address) {
      return {
        ok: false,
        error: {
          type: 'no-result',
          message: `Sin resultado para lon=${lon.toFixed(6)}, lat=${lat.toFixed(6)}`,
        },
      };
    }

    // --- Mapear respuesta ---
    return {
      ok: true,
      data: {
        address: data.address ?? '',
        tip_via: data.tip_via ?? undefined,
        streetName: data.streetName ?? undefined,
        portalNumber: data.portalNumber !== 0 ? String(data.portalNumber) : undefined,
        municipality: data.municipality ?? undefined,
        province: data.province ?? undefined,
        postalCode: data.postalCode ?? undefined,
        wgs84: { lon, lat },
        locationType: data.type ?? undefined,
        state: data.state ?? undefined,
      },
    };
  } catch (err: unknown) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      return {
        ok: false,
        error: {
          type: options.signal?.aborted ? 'network' : 'timeout',
          message: options.signal?.aborted
            ? 'Petición cancelada por el usuario'
            : `Timeout tras ${timeout}ms`,
        },
      };
    }
    return {
      ok: false,
      error: {
        type: 'network',
        message: err instanceof Error ? err.message : 'Error desconocido',
      },
    };
  } finally {
    clearTimeout(timer);
  }
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Combina dos AbortSignal: aborta si cualquiera de los dos aborta.
 */
function combineAbortSignals(
  userSignal: AbortSignal,
  timeoutSignal: AbortSignal,
): AbortSignal {
  const controller = new AbortController();

  const abort = () => controller.abort();

  if (userSignal.aborted || timeoutSignal.aborted) {
    controller.abort();
    return controller.signal;
  }

  userSignal.addEventListener('abort', abort, { once: true });
  timeoutSignal.addEventListener('abort', abort, { once: true });

  return controller.signal;
}
